import { notification, Typography } from 'antd';
import { Formik } from 'formik';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useChangePasswordMutation } from '../../../../api/mutations/changePassword';
import Form from '../../../../components/fields/Form';
import PasswordRequirementList from '../../../../components/PasswordRequirementList';
import { useThemeComponents } from '../../../../themes';
import requirements from '../../../../utilities/passwordRequirement';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';
import { PasswordRequirementsContainer, Container } from '../styled';

const passwordValidator = validators.compose(
    validators.requiredString('previousPassword'),
    validators.requiredString('newPassword'),
    validators.requiredString('newPasswordRepeat'),
    validators.validPasswordFormat('newPassword'),
    validators.matchPassword('newPasswordRepeat', 'newPassword')
);

type FormValues = {
    previousPassword: string;
    newPassword: string;
    newPasswordRepeat: string;
};

const ChangePasswordContent = () => {
    const { t } = useTranslation('userSelf');
    const [mutation] = useChangePasswordMutation();
    const validate = useValidator(passwordValidator);
    const {
        FormFields: { PasswordField },
    } = useThemeComponents();

    const validatePassword = useCallback(
        value =>
            requirements(t).map(({ description, regex }) => ({
                description,
                isChecked: regex.test(value),
            })),
        [t]
    );

    const onSubmit = useHandleError(
        async ({ previousPassword, newPassword }: FormValues) => {
            // display a notification
            const { data } = await mutation({ variables: { previousPassword, newPassword } });
            if (data?.changePassword) {
                notification.success({
                    message: t('userSelf:passwordSettings.successMessage.title'),
                    description: t('userSelf:passwordSettings.successMessage.description'),
                });
            }
        },
        [t, mutation]
    );

    return (
        <Container>
            <Formik
                initialValues={{ previousPassword: '', newPassword: '', newPasswordRepeat: '' }}
                onSubmit={onSubmit}
                validate={validate}
            >
                {({ isSubmitting, handleSubmit, values, isValid }) => (
                    <Form
                        id="changePasswordForm"
                        name="changePasswordForm"
                        onSubmitCapture={handleSubmit}
                        style={{ width: '100%' }}
                    >
                        <div>
                            <PasswordField
                                name="previousPassword"
                                {...t('userSelf:passwordSettings.fields.previousPassword', { returnObjects: true })}
                            />
                            <PasswordField
                                name="newPassword"
                                {...t('userSelf:passwordSettings.fields.newPassword', { returnObjects: true })}
                            />
                            <PasswordField
                                name="newPasswordRepeat"
                                {...t('userSelf:passwordSettings.fields.newPasswordRepeat', { returnObjects: true })}
                            />
                        </div>
                        <PasswordRequirementsContainer>
                            <Typography.Title level={5}>
                                {t('userSelf:passwordSettings.requirements.title')}
                            </Typography.Title>
                            <PasswordRequirementList requirements={validatePassword(values.newPassword)} />
                        </PasswordRequirementsContainer>
                    </Form>
                )}
            </Formik>
        </Container>
    );
};

export default ChangePasswordContent;
